import { useState, useEffect, useCallback, useRef } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VerifiedIcon from '@mui/icons-material/Verified';
import LoginIcon from '@mui/icons-material/Login';
import PlaceIcon from '@mui/icons-material/Place';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import StoreIcon from '@mui/icons-material/Store';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import Cookies from 'js-cookie';
import CircularProgress from '@mui/material/CircularProgress';
import styles from './HeroV4.module.css';
import eventEmitter from '../Home/eventEmitter';
import { BASE_URL_FOR_ELASTIC_SEARCH, BASE_URL_SANDBOX, ADD_DELIVERY_ADDRESS } from '../../Constants/Config';
import { fetchCustomerAddresses, selectCustomerAddresses } from '../../redux/features/CustomerAddress/customerAddressSlice';
import { US_STATES } from '../Header/MiniHeader/US_STATES';
import stateMap from '../Header/MiniHeader/usStates';
import {
  getAddressSuggestionValidationMessage,
  isSelectableAddressSuggestion,
} from '../../utils/placeSuggestions';

const extractAddressDetailsForFetchCordinates = (formattedAddress) => {
  const parts = formattedAddress.split(',').map((part) => part.trim());
  if (parts.length < 3) throw new Error('Please fill proper address');
  let state = '', city = '', streetAddress = '', zipCode = '';
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];
    const zipMatch = part.match(/\b\d{5}\b/);
    if (zipMatch) {
      zipCode = zipMatch[0];
      const statePart = part.replace(zipCode, '').trim();
      state = statePart.length === 2 ? statePart.toUpperCase() : stateMap[statePart];
      if (i > 0) city = parts[i - 1];
      if (i > 1) streetAddress = parts.slice(0, i - 1).join(', ');
      break;
    } else if (US_STATES.includes(part.length === 2 ? part.toUpperCase() : stateMap[part])) {
      state = part.length === 2 ? part.toUpperCase() : stateMap[part];
      if (i > 0) city = parts[i - 1];
      if (i > 1) streetAddress = parts.slice(0, i - 1).join(', ');
      break;
    }
  }
  return { state, zipCode, city, streetAddress };
};

// City- and ZIP-level picks are allowed, so a street line is not required.
const isValidAddress = (addressObj) =>
  Boolean(
    addressObj &&
      addressObj.state &&
      US_STATES.includes(addressObj.state) &&
      addressObj.city,
  );

const hasInlineCoordinates = (suggestion) => {
  const structured = (suggestion?.placePrediction || suggestion)
    ?.structuredFormat;
  return structured?.lat !== undefined && structured?.lng !== undefined;
};

// Build a human-readable label for a suggestion from the elasticsearch response
const getSuggestionText = (place) => {
  const rawText = place?.placePrediction?.text?.text;
  if (rawText) return rawText;

  const structured = place?.placePrediction?.structuredFormat;
  const mainText = structured?.mainText?.text;
  const secondaryText = structured?.secondaryText?.text;

  if (mainText || secondaryText) {
    return [mainText, secondaryText].filter(Boolean).join(', ');
  }

  // Fallback for older payloads
  return '';
};

const resolveZipCodeFromCoordinates = async (latitude, longitude) => {
  if (latitude == null || longitude == null) return '';

  try {
    const response = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
    );

    if (!response.ok) return '';

    const geoData = await response.json();
    return geoData?.postcode || '';
  } catch {
    return '';
  }
};

const HERO_BANNER_LOAD_TIMEOUT_MS = 8000;

/* Crossfade timeline: 3s hold, 1s fade in, 3s hold, 1s fade out. */
const BANNER_HOLD_MS = 3000;
const BANNER_FADE_MS = 1000;
const BANNER_CYCLE_MS = BANNER_HOLD_MS * 2 + BANNER_FADE_MS * 2;
/* Cap the per-frame step so a throttled or backgrounded tab resumes smoothly
   instead of jumping the timeline forward by however long it was away. */
const BANNER_MAX_STEP_MS = 100;

const easeInOut = (progress) => progress * progress * (3 - 2 * progress);

const bannerOpacityAt = (elapsed) => {
  const t = ((elapsed % BANNER_CYCLE_MS) + BANNER_CYCLE_MS) % BANNER_CYCLE_MS;
  if (t < BANNER_HOLD_MS) return 0;
  if (t < BANNER_HOLD_MS + BANNER_FADE_MS) {
    return easeInOut((t - BANNER_HOLD_MS) / BANNER_FADE_MS);
  }
  if (t < BANNER_HOLD_MS * 2 + BANNER_FADE_MS) return 1;
  const fadeOutStart = BANNER_HOLD_MS * 2 + BANNER_FADE_MS;
  return easeInOut(1 - (t - fadeOutStart) / BANNER_FADE_MS);
};

const isBannerDecoded = (image) =>
  Boolean(image && image.complete && image.naturalWidth > 0);

/*
 * The crossfade used to be a CSS animation, and browsers switch those off on
 * their own: `prefers-reduced-motion` is what iOS "Reduce Motion", Android
 * "Remove animations", Windows "Animation effects: off" and Chrome battery
 * saver all report, so the banner sat frozen on a share of devices while it
 * ran fine locally. IsometricShowcase already solved this by writing frames
 * imperatively from requestAnimationFrame; the banner now does the same, so
 * there is no CSS animation left for a browser setting to suppress.
 *
 * Playback still waits for both banners to decode (a slow phone must never
 * fade in a blank layer) and falls back to a single static banner if one of
 * them never arrives.
 */
const useHeroBannerCrossfade = () => {
  const primaryRef = useRef(null);
  const altRef = useRef(null);
  const [status, setStatus] = useState('loading');

  const syncStatus = useCallback(() => {
    if (isBannerDecoded(primaryRef.current) && isBannerDecoded(altRef.current)) {
      setStatus('crossfading');
    }
  }, []);

  const markStatic = useCallback(() => {
    setStatus((prev) => (prev === 'crossfading' ? prev : 'static'));
  }, []);

  useEffect(() => {
    // Cached banners can finish before React attaches the onLoad handlers.
    syncStatus();
    if (typeof window === 'undefined') return undefined;
    const timeoutId = window.setTimeout(
      markStatic,
      HERO_BANNER_LOAD_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timeoutId);
  }, [markStatic, syncStatus]);

  useEffect(() => {
    const node = altRef.current;
    if (status !== 'crossfading' || !node || typeof window === 'undefined') {
      return undefined;
    }

    // Old Android WebViews ship without rAF; a timer keeps them animating.
    const requestFrame = window.requestAnimationFrame
      ? window.requestAnimationFrame.bind(window)
      : (callback) => window.setTimeout(() => callback(Date.now()), 16);
    const cancelFrame = window.cancelAnimationFrame
      ? window.cancelAnimationFrame.bind(window)
      : window.clearTimeout.bind(window);

    let frameId = null;
    let lastTs = null;
    let elapsed = 0;
    let offScreen = false;

    const tick = (ts) => {
      if (lastTs !== null) {
        elapsed += Math.min(ts - lastTs, BANNER_MAX_STEP_MS);
      }
      lastTs = ts;
      node.style.opacity = String(bannerOpacityAt(elapsed));
      frameId = requestFrame(tick);
    };

    const start = () => {
      if (frameId !== null) return;
      lastTs = null;
      frameId = requestFrame(tick);
    };

    const stop = () => {
      if (frameId === null) return;
      cancelFrame(frameId);
      frameId = null;
      lastTs = null;
    };

    const syncPlayback = () => {
      if (document.visibilityState === 'hidden' || offScreen) stop();
      else start();
    };

    let observer = null;
    if (typeof window.IntersectionObserver === 'function') {
      observer = new window.IntersectionObserver((entries) => {
        offScreen = !entries.some((entry) => entry.isIntersecting);
        syncPlayback();
      });
      observer.observe(node);
    }

    document.addEventListener('visibilitychange', syncPlayback);
    start();

    return () => {
      document.removeEventListener('visibilitychange', syncPlayback);
      if (observer) observer.disconnect();
      stop();
      node.style.opacity = '';
    };
  }, [status]);

  return {
    primaryRef,
    altRef,
    onBannerLoad: syncStatus,
    onBannerError: markStatic,
    crossfadeStatus: status,
  };
};

const Hero = ({ onSignInClick, isAuthenticated = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { UserData } = useSelector((state) => state.authUser);
  const existingAddresses = useSelector(selectCustomerAddresses);
  const locationErrorMessage = 'Please enter a valid address.';
  const timeoutRef = useRef(null);
  const {
    primaryRef: bannerPrimaryRef,
    altRef: bannerAltRef,
    onBannerLoad,
    onBannerError,
    crossfadeStatus,
  } = useHeroBannerCrossfade();

  const [searchInput, setSearchInput] = useState('');
  const [places, setPlaces] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [focusedSuggestionIndex, setFocusedSuggestionIndex] = useState(-1);

  // Save address to backend for logged-in users and set as default
  const saveAddressToBackend = useCallback(async (addressData) => {
    if (!isAuthenticated || !UserData?.id) return;
    
    try {
      const resolvedZipCode =
        addressData.zipCode ||
        (await resolveZipCodeFromCoordinates(
          addressData.latitude,
          addressData.longitude,
        ));

      if (!resolvedZipCode) {
        setError('Please select an address with a ZIP code.');
        return;
      }

      const normalizedAddressData = {
        ...addressData,
        zipCode: resolvedZipCode,
      };

      // Check if this address already exists on the backend
      const duplicate = Array.isArray(existingAddresses)
        ? existingAddresses.find(
            (a) =>
              a.a_zip === normalizedAddressData.zipCode &&
              a.a_city?.trim().toLowerCase() === normalizedAddressData.city?.trim().toLowerCase() &&
              a.a_state === normalizedAddressData.state
          )
        : null;

      let addressIdToSetDefault = duplicate?.id;
      const desiredAddressLine = (normalizedAddressData.displayAddress || normalizedAddressData.streetAddress || '').trim();
      const existingAddressLine = (duplicate?.a_address_line_1 || '').trim();

      if (
        duplicate &&
        desiredAddressLine &&
        existingAddressLine.toLowerCase() !== desiredAddressLine.toLowerCase()
      ) {
        const updatePayload = {
          userId: UserData.id,
          Id: duplicate.id,
          cc_f_name: UserData.f_name || UserData.firstname || '',
          cc_l_name: UserData.l_name || UserData.lastname || '',
          cc_phone_no: UserData.phone || '',
          cc_street_address: desiredAddressLine,
          cc_zip_code: normalizedAddressData.zipCode,
          city: normalizedAddressData.city,
          suite_address: '',
          state: normalizedAddressData.state,
          latitude: normalizedAddressData.latitude,
          longitude: normalizedAddressData.longitude,
          placeID: normalizedAddressData.placeId,
          a_country: 'US',
        };

        await axios.post(
          BASE_URL_SANDBOX + ADD_DELIVERY_ADDRESS,
          updatePayload,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }

      if (!duplicate) {
        // Address doesn't exist yet, add it
        const payload = {
          userId: UserData.id,
          address_id: '',
          cc_f_name: UserData.f_name || UserData.firstname || '',
          cc_l_name: UserData.l_name || UserData.lastname || '',
          cc_phone_no: UserData.phone || '',
          cc_street_address: normalizedAddressData.displayAddress || normalizedAddressData.streetAddress,
          cc_zip_code: normalizedAddressData.zipCode,
          city: normalizedAddressData.city,
          suite_address: '',
          state: normalizedAddressData.state,
          latitude: normalizedAddressData.latitude,
          longitude: normalizedAddressData.longitude,
          placeID: normalizedAddressData.placeId,
          a_country: 'US',
        };

        const addResponse = await axios.post(
          BASE_URL_SANDBOX + ADD_DELIVERY_ADDRESS,
          payload,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        addressIdToSetDefault = addResponse?.data?.result?.id;
      }

      // Set address as default
      if (addressIdToSetDefault) {
        await axios.post(
          BASE_URL_SANDBOX + 'Customer_Account/set_default_address',
          { id: addressIdToSetDefault, user_id: UserData.id, merchant_id: '' },
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }

      // Refresh Redux customer addresses so the header picks up the new default
      await dispatch(fetchCustomerAddresses(UserData.id)).unwrap();
    } catch {}
  }, [isAuthenticated, UserData, dispatch, existingAddresses]);

  useEffect(() => {
    const closeDropdown = () => setShowDropdown(false);
    eventEmitter.on('openLocationModal', closeDropdown);
    eventEmitter.on('openAuthModal', closeDropdown);
    return () => {
      eventEmitter.off('openLocationModal', closeDropdown);
      eventEmitter.off('openAuthModal', closeDropdown);
    };
  }, []);

  const filterValidSuggestions = (suggestions) =>
    suggestions.filter((s) => {
      // Never render a suggestion the select handler would reject.
      if (!isSelectableAddressSuggestion(s)) return false;
      // ZIP lookups arrive with coordinates instead of a parseable address.
      if (hasInlineCoordinates(s)) return true;

      const fullText = getSuggestionText(s);
      try {
        const parsed = extractAddressDetailsForFetchCordinates(fullText);
        return isValidAddress(parsed);
      } catch {
        return false;
      }
    });

  const fetchCoordinates = useCallback(async (input) => {
    setError('');
    try {
      const response = await axios.get(
        `${BASE_URL_FOR_ELASTIC_SEARCH}places/autocomplete?input=${input}`,
      );
      if (response.status === 200 && response.data?.suggestions?.length > 0) {
        const filtered = filterValidSuggestions(response.data.suggestions);
        if (filtered.length > 0) {
          setPlaces(filtered);
          setFocusedSuggestionIndex(-1);
          setShowDropdown(true);
        } else {
          setError('Please enter a full street address.');
          setPlaces([]);
          setShowDropdown(false);
        }
      } else {
        setError('Please enter a full street address.');
        setPlaces([]);
        setShowDropdown(false);
      }
    } catch {
      setError('Error fetching location. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = useCallback(
    (e) => {
      const filtered = e.target.value.replace(/[^a-zA-Z0-9,# ]/g, '');
      setSearchInput(filtered);
      setSelectedPlace(null);
      setFocusedSuggestionIndex(-1);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (filtered) {
        setLoading(true);
        setError('');
        timeoutRef.current = setTimeout(() => fetchCoordinates(filtered), 500);
      } else {
        setPlaces([]);
        setShowDropdown(false);
        setLoading(false);
        setError('');
      }
    },
    [fetchCoordinates],
  );

  // Persists the resolved location and moves the user on. Shared by the
  // place-details path and the coordinate-only (ZIP) path.
  const commitSelectedPlace = useCallback(
    async (placeData) => {
      setSelectedPlace(placeData);
      setError('');

      const coords = { lat: placeData.latitude, lng: placeData.longitude };
      Cookies.set('userLocation', JSON.stringify(coords), { expires: 7 });
      Cookies.set('zipCode', JSON.stringify(placeData.zipCode), { expires: 7 });
      Cookies.set('locationCity', placeData.city, { expires: 7 });

      const newAddress = {
        unique_id: Date.now(),
        id: Date.now(),
        a_address_line_1:
          placeData.displayAddress ||
          placeData.streetAddress ||
          `${placeData.zipCode} ${placeData.city}`,
        a_zip: placeData.zipCode,
        a_city: placeData.city,
        a_state: placeData.state,
        latitude: placeData.latitude,
        longitude: placeData.longitude,
        placeID: placeData.placeId,
        is_default: '1',
      };
      const existing = Cookies.get('customerAddresses');
      let addressArray = [newAddress];
      if (existing) {
        try {
          const parsed = JSON.parse(existing);
          if (Array.isArray(parsed)) {
            // Filter out duplicates by zipCode
            const others = parsed
              .filter((a) => a.a_zip !== placeData.zipCode)
              .map((a) => ({ ...a, is_default: '0' }));
            addressArray = [newAddress, ...others];
          }
        } catch {}
      }
      Cookies.set('customerAddresses', JSON.stringify(addressArray), {
        expires: 7,
      });
      // Save to backend for logged-in users
      await saveAddressToBackend(placeData);
      eventEmitter.emit('locationUpdated');
      navigate('/home', { replace: false });
    },
    [navigate, saveAddressToBackend],
  );

  const handlePlaceSelect = useCallback(async (place) => {
    if (isNavigating) return;
    setIsNavigating(true);

    const placeId = place.placePrediction?.placeId;
    const description = getSuggestionText(place);
    setPlaces([]);
    setShowDropdown(false);
    setSearchInput(description);

    const validationMessage = getAddressSuggestionValidationMessage(place);
    if (validationMessage) {
      setError(validationMessage);
      setIsNavigating(false);
      return;
    }

    // ZIP lookups carry coordinates inline and need no place-details call.
    if (!placeId && hasInlineCoordinates(place)) {
      const structured = place?.placePrediction?.structuredFormat;
      const secondaryParts = String(structured?.secondaryText?.text || '')
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
      const resolvedState = secondaryParts
        .map((part) =>
          part.length === 2 ? part.toUpperCase() : stateMap[part] || '',
        )
        .filter((candidate) => US_STATES.includes(candidate))
        .pop();

      const placeData = {
        streetAddress: '',
        displayAddress: description,
        city: secondaryParts[0] || '',
        state: resolvedState || '',
        zipCode:
          String(structured?.mainText?.text ?? '').match(/\d{5}/)?.[0] || '',
        latitude: structured?.lat,
        longitude: structured?.lng,
        placeId: '',
      };

      if (!placeData.city || !placeData.state) {
        setError(locationErrorMessage);
        setIsNavigating(false);
        return;
      }

      try {
        await commitSelectedPlace(placeData);
      } catch {
        setError(locationErrorMessage);
      } finally {
        setIsNavigating(false);
      }
      return;
    }

    if (!placeId) {
      setError(locationErrorMessage);
      setIsNavigating(false);
      return;
    }

    try {
      const response = await axios.get(
        `${BASE_URL_FOR_ELASTIC_SEARCH}places/get_details?input=${placeId}`,
      );

      if (response.status === 200) {
        const locationDetails = response.data;
        const { location } = locationDetails || {};
        const structured = place?.placePrediction?.structuredFormat;
        const structuredAddressText = [
          structured?.mainText?.text,
          structured?.secondaryText?.text,
        ]
          .filter(Boolean)
          .join(', ');
        const formattedAddress =
          locationDetails?.formattedAddress ||
          structuredAddressText ||
          description;
        const parsedAddress = extractAddressDetailsForFetchCordinates(formattedAddress);
        const street = parsedAddress?.streetAddress || '';
        const city = parsedAddress?.city || '';
        const state = parsedAddress?.state || '';
        const zipCode = parsedAddress?.zipCode || '';

        if (!city || !state) {
          setError(locationErrorMessage);
          setIsNavigating(false);
          return;
        }

        const placeData = {
          streetAddress: street,
          displayAddress: formattedAddress,
          city,
          state,
          zipCode,
          latitude: location?.latitude ?? structured?.lat,
          longitude: location?.longitude ?? structured?.lng,
          placeId,
        };
        await commitSelectedPlace(placeData);
        return;
      }
    } catch {
      setError(locationErrorMessage);
    } finally {
      setIsNavigating(false);
    }
  }, [isNavigating, navigate, saveAddressToBackend, commitSelectedPlace]);

  const handleFindStores = useCallback(async () => {
    if (!selectedPlace) {
      setError('Please select an address from the suggestions.');
      return;
    }
    if (isNavigating) return;
    setIsNavigating(true);

    const coords = { lat: selectedPlace.latitude, lng: selectedPlace.longitude };
    Cookies.set('userLocation', JSON.stringify(coords), { expires: 7 });
    Cookies.set('zipCode', JSON.stringify(selectedPlace.zipCode), { expires: 7 });

    const newAddress = {
      unique_id: Date.now(),
      a_address_line_1: selectedPlace.displayAddress || selectedPlace.streetAddress,
      a_zip: selectedPlace.zipCode,
      a_city: selectedPlace.city,
      a_state: selectedPlace.state,
      latitude: selectedPlace.latitude,
      longitude: selectedPlace.longitude,
      placeID: selectedPlace.placeId,
      is_default: '1',
    };
    const existing = Cookies.get('customerAddresses');
    let addressArray = [newAddress];
    if (existing) {
      try {
        const parsed = JSON.parse(existing);
        if (Array.isArray(parsed)) {
          // Filter out duplicates by zipCode
          const others = parsed
            .filter((a) => a.a_zip !== selectedPlace.zipCode)
            .map((a) => ({ ...a, is_default: '0' }));
          addressArray = [newAddress, ...others];
        }
      } catch {}
    }
    Cookies.set('customerAddresses', JSON.stringify(addressArray), { expires: 7 });
    try {
      // Save to backend for logged-in users
      await saveAddressToBackend(selectedPlace);
      eventEmitter.emit('locationUpdated');
      navigate('/home', { replace: false });
    } catch {
      setError(locationErrorMessage);
      setIsNavigating(false);
    }
  }, [selectedPlace, navigate, saveAddressToBackend, isNavigating]);

  const handleInputKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        setShowDropdown(false);
        setFocusedSuggestionIndex(-1);
        return;
      }

      if (!showDropdown || places.length === 0) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        setFocusedSuggestionIndex((currentIndex) => {
          const startingIndex =
            currentIndex < 0
              ? direction > 0
                ? -1
                : 0
              : currentIndex;
          return (startingIndex + direction + places.length) % places.length;
        });
        return;
      }

      if (event.key === 'Enter' && focusedSuggestionIndex >= 0) {
        event.preventDefault();
        handlePlaceSelect(places[focusedSuggestionIndex]);
      }
    },
    [
      focusedSuggestionIndex,
      handlePlaceSelect,
      places,
      showDropdown,
    ],
  );

  return (
    <section
      className={styles.hero}
      aria-labelledby="landing-hero-title"
    >
      <div className={styles.heroBackdropStack} aria-hidden="true">
        <img
          ref={bannerPrimaryRef}
          className={styles.heroBackdrop}
          src="/images/hero-banner-1.jpg"
          alt=""
          width="2400"
          height="870"
          decoding="async"
          fetchpriority="high"
          data-testid="hero-banner-primary"
          onLoad={onBannerLoad}
          onError={onBannerError}
        />
        <img
          ref={bannerAltRef}
          className={`${styles.heroBackdrop} ${styles.heroBackdropAlt}`}
          data-crossfade={crossfadeStatus}
          src="/images/hero-banner-2.jpg"
          alt=""
          width="2400"
          height="1094"
          decoding="async"
          data-testid="hero-banner-alt"
          onLoad={onBannerLoad}
          onError={onBannerError}
        />
      </div>
      <div className={styles.heroOverlay} />
      <div className={styles.heroGlow} aria-hidden="true" />
      <div className={styles.heroContent}>
        <div className={styles.heroInner}>
          <div className={styles.heroEyebrow}>
            <AutoAwesomeIcon sx={{ fontSize: 15 }} aria-hidden="true" />
            Shop local. Get it fast.
          </div>
          <h1 id="landing-hero-title" className={styles.heroTitle}>
            Local smoke &amp; vape,
            <span className={styles.heroTitleHighlight}>
              {' '}
              delivered your way.
            </span>
          </h1>
          <p className={styles.heroSubtitle}>
            Discover live inventory from trusted shops nearby, then choose
            convenient pickup or delivery to your door.
          </p>
          <form
            className={styles.searchBox}
            onSubmit={(event) => {
              event.preventDefault();
              handleFindStores();
            }}
          >
            <div className={styles.searchBoxHeader}>
              <label htmlFor="landing-address" className={styles.searchLabel}>
                Where should we look?
              </label>
              <span className={styles.searchCoverage}>Across the U.S.</span>
            </div>
            <div className={styles.searchInputWrapper}>
              <div className={styles.inputWrap}>
                <PlaceIcon
                  className={styles.inputIcon}
                  sx={{ fontSize: 22 }}
                  aria-hidden="true"
                />
                <input
                  id="landing-address"
                  type="text"
                  value={searchInput}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  onFocus={() => {
                    if (places.length > 0) {
                      setShowDropdown(true);
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                  placeholder="Enter a street address"
                  className={styles.searchInput}
                  autoComplete="off"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={showDropdown}
                  aria-controls="landing-address-suggestions"
                  aria-activedescendant={
                    focusedSuggestionIndex >= 0
                      ? `landing-address-option-${focusedSuggestionIndex}`
                      : undefined
                  }
                />
                {loading && (
                  <div className={styles.loader} aria-label="Finding addresses">
                    <CircularProgress size={16} />
                  </div>
                )}
                {showDropdown && places.length > 0 && (
                  <ul
                    id="landing-address-suggestions"
                    role="listbox"
                    aria-label="Address suggestions"
                    className={styles.dropdown}
                  >
                    {places.map((place, index) => {
                      const structured = place?.placePrediction?.structuredFormat;
                      const mainText = structured?.mainText?.text || getSuggestionText(place);
                      const secondaryText = structured?.secondaryText?.text || '';

                      return (
                        <li
                          key={index}
                          id={`landing-address-option-${index}`}
                          role="option"
                          aria-selected={focusedSuggestionIndex === index}
                          className={`${styles.dropdownItem} ${
                            focusedSuggestionIndex === index
                              ? styles.dropdownItemActive
                              : ''
                          }`}
                          onMouseEnter={() => setFocusedSuggestionIndex(index)}
                          onMouseDown={(event) => {
                            event.preventDefault();
                            handlePlaceSelect(place);
                          }}
                        >
                          <span className={styles.dropdownItemIconWrapper}>
                            <PlaceIcon
                              sx={{ fontSize: 14 }}
                              aria-hidden="true"
                            />
                          </span>
                          <span className={styles.dropdownItemText}>
                            <span className={styles.dropdownItemPrimary}>{mainText}</span>
                            {secondaryText && (
                              <span className={styles.dropdownItemSecondary}>{secondaryText}</span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <button
                type="submit"
                className={styles.searchBtn}
                disabled={isNavigating}
              >
                {isNavigating ? (
                  <>
                    <CircularProgress
                      size={16}
                      color="inherit"
                      aria-hidden="true"
                    />
                    Finding stores…
                  </>
                ) : (
                  <>
                    Explore nearby
                    <ArrowForwardIcon sx={{ fontSize: 18 }} aria-hidden="true" />
                  </>
                )}
              </button>
            </div>
            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            <div className={styles.heroActions}>
              {!isAuthenticated && (
                <span className={styles.signInText}>
                  <LoginIcon
                    sx={{ fontSize: 15 }}
                    aria-hidden="true"
                  />
                  <button
                    type="button"
                    className={styles.signInLink}
                    onClick={onSignInClick}
                  >
                    Sign in
                  </button>
                  for saved addresses
                </span>
              )}
            </div>
          </form>
          <div className={styles.heroAssurances} aria-label="Shopping benefits">
            <span>
              <StoreIcon sx={{ fontSize: 15 }} aria-hidden="true" />
              Local inventory
            </span>
            <span>
              <VerifiedUserIcon sx={{ fontSize: 15 }} aria-hidden="true" />
              Secure checkout
            </span>
            <span>
              <VerifiedIcon sx={{ fontSize: 15 }} aria-hidden="true" />
              Age verified
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
